// JobDetailPage — the single-page "file" for a job.
//
// 6-card layout across 2 columns:
//
//   LEFT COLUMN                    RIGHT COLUMN
//   ┌─────────────────┐            ┌─────────────────┐
//   │ Card 1: Main    │            │ Card 4: Schedule │
//   │ (stepper,status │            │                  │
//   │  customer,site) │            │                  │
//   ├─────────────────┤            ├─────────────────┤
//   │ Card 2: Estimate│            │ Card 5: Complete │
//   │ (crew BoM)      │            │ (pulled items)   │
//   ├─────────────────┤            ├─────────────────┤
//   │ Card 3: Quote   │            │ Card 6: Attach.  │
//   │ (build / PDF /  │            │ (sketch,stubs)   │
//   │  mark accepted) │            │                  │
//   └─────────────────┘            └─────────────────┘
//
// Quotes page is REMOVED — all quote logic lives in Card 3 of this page.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight,
  MapPin,
  Calendar,
  Hash,
  FileText,
  Phone,
  Mail,
  ChevronDown,
  Calculator,
  Trash2,
  CheckCircle2,
  Package,
  Paperclip,
  RotateCw,
  ZoomIn,
  ZoomOut,
  StickyNote,
  Check as CheckIcon,
  Loader2,
  Receipt,
  Layers,
  Plus,
} from 'lucide-react';
import { PricingCalculator } from '../components/PricingCalculator.js';
import { JobScheduleSection } from '../components/JobScheduleSection.js';
import {
  PhasesTimelineCard,
  RemainingPoolCard,
} from '../components/phases/PhaseUI.js';
import { WarrantyVisitsCard } from '../components/cards/WarrantyVisitsCard.js';
import { TimeEntriesCard } from '../components/TimeEntriesCard.js';
import { JobDocumentsSection } from '../components/documents/JobDocumentsSection.js';
import {
  getJobDetail,
  getJobPhases,
  getCustomers,
  patchJob,
  deleteJob,
  generateJobPdf,
  getJobPdfUrl,
  getJobSketchUrl,
  getCalendarAssignments,
  setJobInvoice,
  postMiscLine,
  deleteMiscLine,
} from '../api/client.js';
import {
  type JobStatus,
  type JobFieldEstimateRow,
  type JobDetail,
  type CanonicalUom,
  type MiscLineCreateInput,
  unitLabel,
  CANONICAL_UOMS,
} from '@gutterinv/shared';
import { Card } from '../components/ui/card.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { Skeleton } from '../components/ui/skeleton.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.js';
import {
  formatCents,
  formatDate,
  formatInt,
  postTaxToSubtotalCents,
} from '../lib/format.js';
import { JobStageStepper, prevStageTarget } from '../components/JobStageStepper.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { CustomerSearchSelect } from '../components/CustomerSearchSelect.js';
import { seedSkuIdForEstimate } from '../lib/sku-line-presets.js';
import { productLineLabel } from '../lib/time-entry-format.js';
import { qk } from '../lib/queryKeys.js';
import { InlineJobNameEditor } from '../components/InlineJobNameEditor.js';
import { isInstallSchedulable } from '../lib/calendar-dates.js';

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // --- State ---
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [pdfVersion, setPdfVersion] = useState<'draft' | 'frozen'>('draft');
  // Seed with a unique per-mount token so the iframe's PDF URL ALWAYS
  // carries a cache-buster — even on first load. Without this, the initial
  // src was the bare `?version=draft` URL, which is exactly the URL Firefox
  // cached the old (pre-white-bg) black PDF at, so it re-served the stale
  // black copy. A fresh token dodges that cache entry every mount.
  const [iframeKey, setIframeKey] = useState(() => Date.now());
  const [disqualifyOpen, setDisqualifyOpen] = useState(false);
  // Confirm dialog for reverting a finished job backward (un-closing a
  // completed/invoiced job). Holds the target status, or null when closed.
  const [revertConfirm, setRevertConfirm] = useState<JobStatus | null>(null);
  // Inline-edit state for the Quote card amount. Only usable pre-freeze
  // (status < quoted); after that the figure is locked because the
  // frozen PDF already reflects what the customer received.
  const [editingQuoteAmount, setEditingQuoteAmount] = useState(false);
  const [quoteAmountDraft, setQuoteAmountDraft] = useState('');
  const [editingLaborFootage, setEditingLaborFootage] = useState(false);
  const [laborFootageDraft, setLaborFootageDraft] = useState('');
  const [editingLaborRate, setEditingLaborRate] = useState(false);
  const [laborRateDraft, setLaborRateDraft] = useState('');
  const [estimateExpanded, setEstimateExpanded] = useState(true);
  const [quoteExpanded, setQuoteExpanded] = useState(true);
  const [sketchExpanded, setSketchExpanded] = useState(true);
  const [sketchLightbox, setSketchLightbox] = useState(false);
  const [sketchRotation, setSketchRotation] = useState(0);
  const [sketchZoom, setSketchZoom] = useState(1);
  const [sketchPan, setSketchPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  // Customer reassignment (Customer & Site card). editingCustomer toggles the
  // inline picker; customerDraft holds the chosen customerId; reassignConfirm
  // gates the swap behind a confirmation when a quote has already been sent.
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [customerDraft, setCustomerDraft] = useState('');
  const [reassignConfirm, setReassignConfirm] = useState(false);

  // Escape closes lightbox
  useEffect(() => {
    if (!sketchLightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSketchLightbox(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sketchLightbox]);

  // Escape navigates back to /jobs — but ONLY when nothing overlay-like is open and
  // the user isn't typing. Gate at EVENT time on the live DOM (any open Radix dialog,
  // dropdown/popover, or the lightbox) rather than a hardcoded state list — that list
  // silently missed new dialogs (e.g. the calculator's "Clear all" confirm) and a
  // close-race where this listener fires for the same Escape that dismisses a dialog,
  // sending the user out of the job entirely. The dialog is still in the DOM at the
  // moment of keydown (React removes it on the next render), so this reliably bails.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (
        sketchLightbox ||
        quoteDialogOpen ||
        disqualifyOpen ||
        revertConfirm !== null ||
        editingCustomer ||
        document.querySelector(
          '[role="dialog"],[role="listbox"],[role="menu"],[data-radix-popper-content-wrapper]',
        )
      ) {
        return;
      }
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      navigate('/jobs');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sketchLightbox, quoteDialogOpen, disqualifyOpen, revertConfirm, editingCustomer, navigate]);

  // Pan handlers — drag to pan when zoomed
  const handlePanStart = useCallback((e: React.MouseEvent) => {
    if (sketchZoom <= 1) return;
    e.preventDefault();
    setIsPanning(true);
    setPanStart({ x: e.clientX - sketchPan.x, y: e.clientY - sketchPan.y });
  }, [sketchZoom, sketchPan]);

  const handlePanMove = useCallback((e: React.MouseEvent) => {
    if (!isPanning) return;
    setSketchPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
  }, [isPanning, panStart]);

  const handlePanEnd = useCallback(() => {
    setIsPanning(false);
  }, []);

  // --- Data ---
  const { data: job, isLoading, error } = useQuery({
    queryKey: qk.jobs.detail(id!),
    queryFn: () => getJobDetail(id!),
    enabled: !!id,
  });

  // Phase 1.5 Item 19: drive the stepper's "Accepted" revert-pill pre-
  // emptive grey-out when reverting from `scheduled` would orphan an
  // install assignment. Use the SAME queryKey + date range as
  // JobScheduleSection so we share its cache when that component is
  // mounted (gutter Schedule card, or insulation at status='created').
  // When it ISN'T mounted (insulation post-creation, where Phase 1.4
  // hides the Schedule card), this useQuery is the populator.
  const { data: allAssignments } = useQuery({
    queryKey: qk.calendar.assignmentsAllDates(),
    queryFn: () => getCalendarAssignments('2020-01-01', '2030-01-01'),
    staleTime: 30_000,
  });
  // Migration 0061: phases now apply to gutter too. Fetch the job's phases
  // (shared cache key with PhaseUI) to gate the install rendering. A gutter job
  // is "phased" once it has ≥1 phase; "has a legacy install chip" when an
  // install assignment exists but there are no phases (the worker's mutual-
  // exclusion guards guarantee these never coexist).
  const { data: phasesData } = useQuery({
    queryKey: ['job-phases', id!],
    queryFn: () => getJobPhases(id!),
    enabled: !!id && !!job && (job.productLine === 'insulation' || job.productLine === 'gutter'),
  });
  const hasPhases = (phasesData?.phases.length ?? 0) > 0;
  const hasInstallAssignment = useMemo(
    () =>
      !!job &&
      (allAssignments ?? []).some((a) => a.job.id === job.id && a.purpose === 'install'),
    [allAssignments, job],
  );
  // A gutter job with a non-phased install chip (legacy / scheduled the old way).
  const hasLegacyInstallChip = !!job && job.productLine === 'gutter' && !hasPhases && hasInstallAssignment;
  // Whether to drive install through the Phases card: insulation always; gutter
  // once it has phases OR is install-schedulable AND not already on the legacy
  // chip path. Legacy-chip gutter jobs keep the old calendar install flow.
  const usePhasedInstall =
    !!job &&
    (job.productLine === 'insulation' ||
      (job.productLine === 'gutter' &&
        (hasPhases || (isInstallSchedulable(job.status) && !hasLegacyInstallChip))));

  const acceptRevertBlocked = useMemo(() => {
    if (!job || job.status !== 'scheduled') return false;
    return (allAssignments ?? []).some(
      (a) => a.job.id === job.id && a.purpose === 'install',
    );
  }, [allAssignments, job]);

  // A completed estimate VISIT (job_assignment purpose='estimate') cascade-deletes
  // with the job — used to enrich the delete confirm so the office knows that
  // completed visit leaves the calendar/history. Fail-safe: undefined assignments
  // → false → plain message (under-warn, never falsely warn).
  const hasCompletedEstimateVisit = useMemo(
    () =>
      (allAssignments ?? []).some(
        (a) => a.job.id === job?.id && a.purpose === 'estimate' && a.completedAt != null,
      ),
    [allAssignments, job],
  );

  // Phased-job status grey-out: which manual transitions to disable + why.
  // Office-install (2026-10-07, owner ruling): the office clicks Installed (and
  // back) on ANY job, phased or not — the server completes / uncompletes every
  // phase itself, so 'installed' and 'scheduled' are no longer blocked here.
  // The memo shape is kept so the stepper prop + JobInvoiceCard's `phased`
  // flag are unchanged.
  const isInsulation = job?.productLine === 'insulation';
  const isPhasedJob = isInsulation || (job?.productLine === 'gutter' && hasPhases);
  const blockedTargets = useMemo<Partial<Record<JobStatus, string>> | undefined>(() => {
    if (!job || !isPhasedJob) return undefined;
    const blocked: Partial<Record<JobStatus, string>> = {};
    // Migration 0052: the installed → invoiced flip is MANUAL + UNGATED
    // (the app no longer generates per-phase invoices), so 'invoiced' is not
    // blocked either.
    return Object.keys(blocked).length > 0 ? blocked : undefined;
  }, [job, isPhasedJob]);

  // --- Mutations ---
  const deleteMutation = useMutation({
    mutationFn: () => deleteJob(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.jobs.all() });
      void queryClient.invalidateQueries({ queryKey: qk.dashboard.today() });
      navigate('/jobs');
    },
  });

  const generatePdfMutation = useMutation({
    mutationFn: () => generateJobPdf(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.jobs.detail(id!) });
      void queryClient.invalidateQueries({ queryKey: qk.jobs.all() });
      setIframeKey((k) => k + 1);
    },
  });

  const quoteAmountMutation = useMutation({
    mutationFn: (cents: number) => patchJob(id!, { quoteAmountCents: cents }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.jobs.detail(id!) });
      void queryClient.invalidateQueries({ queryKey: qk.jobs.all() });
      setEditingQuoteAmount(false);
      // PDF total reflects the quote amount, so regenerate the draft
      // so the iframe preview matches the new value.
      generatePdfMutation.mutate();
    },
  });

  // Labor footage override (migration 0054). null clears → derive (gutter) /
  // none (insulation); a number pins it. Same edit window + draft-regen as the
  // quote amount; editing in 'quoted' auto-reverts to 'estimated' server-side.
  const laborFootageMutation = useMutation({
    mutationFn: (footage: number | null) =>
      patchJob(id!, { quoteLaborFootageOverride: footage }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.jobs.detail(id!) });
      void queryClient.invalidateQueries({ queryKey: qk.jobs.all() });
      setEditingLaborFootage(false);
      generatePdfMutation.mutate();
    },
  });

  // Per-job rate override (migration 0054). null clears → tenant default; a
  // number (cents/unit) pins it. Same edit window + draft-regen as the footage.
  const laborRateMutation = useMutation({
    mutationFn: (rateCents: number | null) =>
      patchJob(id!, { quoteLaborRateCentsPerUnitOverride: rateCents }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.jobs.detail(id!) });
      void queryClient.invalidateQueries({ queryKey: qk.jobs.all() });
      setEditingLaborRate(false);
      generatePdfMutation.mutate();
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({
      jobId,
      status,
      invoiceNumber,
    }: {
      jobId: string;
      status: JobStatus;
      invoiceNumber?: string | null;
    }) =>
      patchJob(jobId, {
        status,
        ...(invoiceNumber !== undefined ? { invoiceNumber } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.jobs.all() });
      void queryClient.invalidateQueries({ queryKey: qk.jobs.detail(id!) });
      void queryClient.invalidateQueries({ queryKey: qk.dashboard.today() });
    },
  });

  // --- Customer reassignment (Customer & Site card) ---
  // Reuse the same customers source as the create-job dialog. Fetched only
  // while the inline picker is open.
  const customersQuery = useQuery({
    queryKey: qk.customers.all(),
    queryFn: getCustomers,
    enabled: editingCustomer,
  });

  const customerMutation = useMutation({
    mutationFn: (customerId: string) => patchJob(id!, { customerId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.jobs.all() });
      void queryClient.invalidateQueries({ queryKey: qk.jobs.detail(id!) });
      void queryClient.invalidateQueries({ queryKey: qk.dashboard.today() });
      setEditingCustomer(false);
      setReassignConfirm(false);
    },
  });

  // Save: no-op if unchanged; gate behind a confirmation when a quote has
  // already been SENT. Gate on quoteSentAt (NOT status): a quoted→estimated
  // re-quote revert preserves quote_sent_at, so a status check would leak.
  const handleSaveCustomer = useCallback(() => {
    if (!job) return;
    if (!customerDraft || customerDraft === job.customer.id) {
      setEditingCustomer(false);
      return;
    }
    if (job.quoteSentAt) {
      setReassignConfirm(true);
      return;
    }
    customerMutation.mutate(customerDraft);
  }, [job, customerDraft, customerMutation]);

  // Invoice-number modal: fires on the installed → invoiced transition for
  // gutter jobs. Migration 0052: the app no longer generates invoices, so the
  // office invoices in QuickBooks and types the QBO invoice number here. The
  // worker stamps invoiced_at to NOW() when none is supplied (drives the
  // overdue-payment aging clock).
  const [invoiceSentOpen, setInvoiceSentOpen] = useState(false);
  const [invoiceNumberDraft, setInvoiceNumberDraft] = useState<string>('');

  // Notes draft state — textarea holds in-progress edits; autosave fires
  // on blur via the mutation below. Null notes from the server are
  // coerced to empty strings so the textarea can bind.
  const [notesDraft, setNotesDraft] = useState<string>('');
  useEffect(() => {
    setNotesDraft(job?.notes ?? '');
  }, [job?.notes]);

  const notesMutation = useMutation({
    mutationFn: (notes: string) => patchJob(id!, { notes: notes.length === 0 ? null : notes }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.jobs.detail(id!) });
    },
  });

  // --- Loading / error ---
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <Card className="border-destructive p-6">
        <h2 className="text-lg font-semibold text-destructive">Failed to load job</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/jobs')}>
          Back to Jobs
        </Button>
      </Card>
    );
  }

  const hasQuote = job.quoteAmountCents !== null;
  // Deletable through 'quoted'; from 'accepted' onward the job has committed
  // history (calendar/install/invoices) and must be disqualified, not deleted.
  const canDeleteJob =
    job.status === 'created' || job.status === 'estimated' || job.status === 'quoted';
  // Estimate card = ORIGINAL crew estimate only (context='quote',
  // status='confirmed'). Pull-removal (2026-06-09) dropped the "Completed
  // (pulled materials)" card and the install-context 'pulled' rows it read.
  const confirmedEstimates = job.fieldEstimates.filter(
    (e) => e.context === 'quote' && e.status === 'confirmed',
  );

  return (
    <div>
      {/* ============================================================ */}
      {/* STICKY HEADER — breadcrumb + stage stepper                   */}
      {/* Persists at the top while the 6-card grid scrolls below.     */}
      {/* Uses negative margins to break out of the main content's     */}
      {/* px-8 padding so the bar spans the full viewport width, then  */}
      {/* re-applies inner padding to keep content aligned.            */}
      {/* ============================================================ */}
      <div className="sticky top-0 z-20 -mx-8 border-b border-border bg-background px-8 pb-4 pt-4">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Link to="/jobs" className="hover:text-foreground transition-colors">
            Jobs
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <InlineJobNameEditor jobId={job.id} value={job.displayName} variant="breadcrumb" />
        </nav>

        {/* Stage stepper — originally sized to one column width (50%).
            Widened to 78% (+25% → +25%) after adding the post-0015
            stages so the 7 pills + 6 connectors have room to breathe.
            Connector lines carry flex-1 at the top level of the
            stepper flex container, so the extra width distributes
            equally between every pair of pills. */}
        <div className="mt-3 max-w-[calc(78%-0.75rem)]">
          <JobStageStepper
            currentStatus={job.status}
            disqualifiedFromStatus={job.disqualifiedFromStatus}
            productLine={job.productLine}
            disabled={statusMutation.isPending}
            hasEstimateItems={confirmedEstimates.length > 0}
            hasDraftPdf={job.hasDraftPdf}
            acceptRevertBlocked={acceptRevertBlocked}
            blockedTargets={blockedTargets}
            onTransition={(to) => {
              // installed → invoiced captures the QBO invoice number (the
              // app no longer generates invoices) via the modal. The revert
              // completed → invoiced bypasses it.
              if (to === 'invoiced' && job.status === 'installed') {
                setInvoiceNumberDraft(job.externalInvoiceNumber ?? '');
                setInvoiceSentOpen(true);
                return;
              }
              // Confirm before stepping a finished job backward
              // (un-closing a completed/invoiced job in one click).
              const isRevert = to === prevStageTarget(job.status);
              if (isRevert && (job.status === 'completed' || job.status === 'invoiced')) {
                setRevertConfirm(to);
                return;
              }
              statusMutation.mutate({ jobId: job.id, status: to });
            }}
            onDisqualify={() => setDisqualifyOpen(true)}
            onReopen={(to) => statusMutation.mutate({ jobId: job.id, status: to })}
          />
          {statusMutation.error && (
            <div className="mt-2 text-xs text-destructive">
              {statusMutation.error instanceof Error
                ? statusMutation.error.message
                : 'Status update failed'}
            </div>
          )}
          <p className="mt-2 flex items-center gap-3 text-xs text-taupe">
            <span>Created {formatDate(job.createdAt)}</span>
            {/* Invoice-sent date surfaces once the office advances the
                job to 'invoiced' via the modal. Persistent visibility
                here (sticky header) means the date is visible while
                scrolling through the detail cards below. */}
            {job.invoicedAt && (
              <>
                <span>·</span>
                <span>
                  Invoice sent{' '}
                  <span className="font-semibold text-foreground">
                    {formatDate(job.invoicedAt)}
                  </span>
                </span>
              </>
            )}
          </p>
        </div>
      </div>

      {/* ============================================================ */}
      {/* 2-COLUMN GRID (scrollable content below the sticky header)   */}
      {/* ============================================================ */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">

        {/* ======== LEFT COLUMN ======== */}
        <div className="space-y-6">

          {/* ---- CARD 1: MAIN (Status + Customer & Site) ---- */}
          <Card className="p-5">
            {/* Customer & Site (status now lives in the sticky-header stepper).
                Two columns — customer LEFT, site RIGHT — so the card stays short
                and uses the full width. Stacks on narrow viewports. */}
            <div>
              <h3 className="eyebrow mb-3">Customer & Site</h3>
              <div className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                {/* Customer */}
                <div className="space-y-1.5">
                  {editingCustomer ? (
                    <div
                      className="space-y-2"
                      onKeyDown={(e) => {
                        if (e.key === 'Escape' && !customerMutation.isPending)
                          setEditingCustomer(false);
                      }}
                    >
                      <CustomerSearchSelect
                        value={customerDraft}
                        customers={customersQuery.data ?? []}
                        onChange={setCustomerDraft}
                        disabled={customerMutation.isPending}
                        placeholder="Choose a customer…"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleSaveCustomer}
                          disabled={
                            customerMutation.isPending ||
                            !customerDraft ||
                            customerDraft === job.customer.id
                          }
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingCustomer(false)}
                          disabled={customerMutation.isPending}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-foreground">{job.customer.name}</div>
                        <button
                          type="button"
                          className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                          onClick={() => {
                            setCustomerDraft(job.customer.id);
                            setEditingCustomer(true);
                          }}
                        >
                          Change
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                        <span className="capitalize">{job.customer.type}</span>
                        {job.customer.discountPct > 0 && (
                          <span className="tnum">{job.customer.discountPct}% discount</span>
                        )}
                      </div>
                      {job.customer.phone && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Phone className="h-3 w-3" />
                          {job.customer.phone}
                        </div>
                      )}
                      {job.customer.email && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Mail className="h-3 w-3" />
                          {job.customer.email}
                        </div>
                      )}
                    </>
                  )}
                </div>
                {/* Site */}
                <div className="space-y-1.5">
                  <div className="flex items-start gap-1.5">
                    <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-taupe" />
                    <span className="text-foreground">{job.address}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Layers className="h-3 w-3 text-taupe" />
                    <span>{productLineLabel(job.productLine)}</span>
                  </div>
                  {job.poNumber && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Hash className="h-3 w-3 text-taupe" />
                      <span>PO {job.poNumber}</span>
                    </div>
                  )}
                  {/* Install date intentionally not shown here — the Schedule card
                      below shows estimate + install dates explicitly. */}
                </div>
              </div>
            </div>

            {/* Delete — allowed through 'quoted'; muted (disabled) from
                'accepted' onward (committed history → disqualify instead). */}
            <div className="mt-4 border-t border-border pt-4">
              <Button
                variant="outline"
                size="sm"
                className="w-full hover:border-destructive hover:bg-destructive/10 hover:text-destructive disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={deleteMutation.isPending || !canDeleteJob}
                title={
                  canDeleteJob
                    ? undefined
                    : 'Past Quoted — this job has committed history. Disqualify it instead.'
                }
                onClick={() => {
                  const msg = hasCompletedEstimateVisit
                    ? 'Delete this job? It also has a completed estimate visit that will be removed from the calendar/history. This cannot be undone.'
                    : 'Delete this job? This cannot be undone.';
                  if (window.confirm(msg)) {
                    deleteMutation.mutate();
                  }
                }}
              >
                <Trash2 className="h-4 w-4" />
                {deleteMutation.isPending ? 'Deleting...' : 'Delete job'}
              </Button>
              {deleteMutation.error && (
                <div className="mt-2 text-xs text-destructive">
                  {deleteMutation.error instanceof Error
                    ? deleteMutation.error.message
                    : 'Delete failed'}
                </div>
              )}
            </div>
          </Card>

          {/* ---- CARD 2: ESTIMATE (crew BoM) ---- */}
          <Card className="p-5">
            <button
              onClick={() => setEstimateExpanded(!estimateExpanded)}
              className="flex w-full items-center gap-1.5 text-left"
            >
              <ChevronDown className={'h-3.5 w-3.5 text-muted-foreground transition-transform' + (estimateExpanded ? '' : ' -rotate-90')} />
              <h3 className="eyebrow flex items-center gap-1.5">
                <Package className="h-3 w-3" />
                Estimate
                {confirmedEstimates.length > 0 && (
                  <span className="ml-1 text-muted-foreground">
                    ({confirmedEstimates.length})
                  </span>
                )}
              </h3>
            </button>
            {estimateExpanded && (
              <div className="mt-3 space-y-3">
                {confirmedEstimates.length > 0 ? (
                  <>
                    <div className="rounded-md border border-border">
                      {confirmedEstimates.map((e) => (
                        <FieldEstimateRow key={e.id} estimate={e} />
                      ))}
                    </div>
                    {job.estimatedPriceCents > 0 && (
                      <div className="flex items-center justify-between rounded-md border border-border bg-secondary/30 px-3 py-2">
                        <span className="text-sm font-medium text-foreground">Materials total</span>
                        <span className="tnum text-base font-bold text-foreground">
                          {formatCents(job.estimatedPriceCents)}
                        </span>
                      </div>
                    )}
                    {job.estimatedCostCents !== null && job.estimatedCostCents > 0 && (
                      <div className="flex items-center justify-between px-3 text-xs text-muted-foreground">
                        <span>Estimated cost basis (owner)</span>
                        <span className="tnum">{formatCents(job.estimatedCostCents)}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-secondary/20 py-8">
                    <Package className="h-6 w-6 text-taupe" />
                    <span className="text-sm text-muted-foreground">
                      No field estimates yet
                    </span>
                    <span className="text-xs text-taupe">
                      Crew submits via /estimate in Telegram
                    </span>
                  </div>
                )}

                {/* Customer labor (migration 0054), relocated from the Quote
                    card (Rev 1.2). Estimate = Material AND Labor. Gutter footage
                    derives from the ft estimate lines (manual override allowed);
                    insulation SF is hand-entered; rate = per-job override ??
                    global. The combined material+labor total is the editable
                    value on the Quote card — NOT shown here (the two materials
                    bases, estimatedPriceCents vs quoteAmountCents, can differ).
                    Gate includes hasQuote so TEMPLATE jobs (quote set, 0 estimate
                    lines) still get the editors. */}
                {(hasQuote || confirmedEstimates.length > 0) && (() => {
                  const isPreFreeze =
                    job.status === 'created' ||
                    job.status === 'estimated' ||
                    job.status === 'quoted';
                  const hasLabor = job.quoteLaborChargeCents > 0;
                  if (!isPreFreeze && !hasLabor) return null;
                  const unit = job.productLine === 'insulation' ? 'SF' : 'LF';
                  const isOverridden = job.quoteLaborFootageOverride !== null;
                  // Insulation labor footage = Σ actual SF over confirmed batting
                  // lines. Prefer the office-entered SF (migration 0067); fall back
                  // to qty × coverage_sf (full-bag SF) for legacy/bot lines. MUST
                  // match the server's derivedLaborFootageSql (jobs-shared.ts) or
                  // the displayed footage won't equal the frozen quote.
                  const battingLines =
                    job.productLine === 'insulation'
                      ? job.fieldEstimates.filter(
                          (e) =>
                            e.context === 'quote' &&
                            e.status === 'confirmed' &&
                            e.coverageSf != null,
                        )
                      : [];
                  const derivedSf = battingLines.reduce(
                    (acc, e) => acc + (e.enteredSf ?? e.quantity * (e.coverageSf ?? 0)),
                    0,
                  );
                  const commitFootage = () => {
                    const cleaned = laborFootageDraft.replace(/[^0-9.]/g, '');
                    if (cleaned.trim() === '') {
                      // Empty → clear the override (gutter: derive; insulation: none).
                      if (isOverridden) laborFootageMutation.mutate(null);
                      else setEditingLaborFootage(false);
                      return;
                    }
                    const num = Number(cleaned);
                    if (!Number.isFinite(num) || num < 0) {
                      setEditingLaborFootage(false);
                      return;
                    }
                    laborFootageMutation.mutate(num);
                  };
                  // Per-job RATE override. Effective rate = override ?? tenant
                  // (server-resolved). Editor seeds BLANK when no override so
                  // saving unchanged never mints a spurious override = tenant.
                  const rateOverridden = job.quoteLaborRateCentsPerUnitOverride !== null;
                  const effectiveRateDollars = (job.laborRateCentsPerUnit / 100).toFixed(2);
                  const commitRate = () => {
                    const cleaned = laborRateDraft.replace(/[^0-9.]/g, '');
                    if (cleaned.trim() === '') {
                      // Empty → revert to the global/tenant default.
                      if (rateOverridden) laborRateMutation.mutate(null);
                      else setEditingLaborRate(false);
                      return;
                    }
                    const dollars = Number(cleaned);
                    if (!Number.isFinite(dollars) || dollars < 0) {
                      setEditingLaborRate(false);
                      return;
                    }
                    laborRateMutation.mutate(Math.round(dollars * 100));
                  };
                  return (
                    <>
                    <div className="rounded-md border border-border px-3 py-2 text-sm space-y-1.5">
                      {/* Labor Rate ($/unit) — effective = override ?? global. */}
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground">Labor Rate</span>
                        {editingLaborRate ? (
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">$</span>
                            <Input
                              autoFocus
                              inputMode="decimal"
                              value={laborRateDraft}
                              onChange={(e) => setLaborRateDraft(e.target.value)}
                              onBlur={commitRate}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  commitRate();
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  setEditingLaborRate(false);
                                }
                              }}
                              disabled={laborRateMutation.isPending}
                              className="tnum h-8 w-24 text-right"
                            />
                            <span className="text-muted-foreground">/{unit}</span>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              if (!isPreFreeze) return;
                              // Seed BLANK when no override (placeholder = effective).
                              setLaborRateDraft(
                                rateOverridden
                                  ? String((job.quoteLaborRateCentsPerUnitOverride ?? 0) / 100)
                                  : '',
                              );
                              setEditingLaborRate(true);
                            }}
                            className={
                              'tnum font-medium ' +
                              (isPreFreeze ? 'cursor-text hover:text-primary' : 'cursor-default')
                            }
                            title={isPreFreeze ? 'Click to edit' : 'Locked once sent'}
                          >
                            ${effectiveRateDollars} /{unit}
                          </button>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground">
                          {job.productLine === 'insulation' ? 'Insulation area' : 'Labor footage'}
                        </span>
                        {editingLaborFootage ? (
                          <div className="flex items-center gap-1">
                            <Input
                              autoFocus
                              inputMode="decimal"
                              value={laborFootageDraft}
                              onChange={(e) => setLaborFootageDraft(e.target.value)}
                              onBlur={commitFootage}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  commitFootage();
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  setEditingLaborFootage(false);
                                }
                              }}
                              disabled={laborFootageMutation.isPending}
                              className="tnum h-8 w-24 text-right"
                            />
                            <span className="text-muted-foreground">{unit}</span>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              if (!isPreFreeze) return;
                              setLaborFootageDraft(
                                isOverridden ? String(job.quoteLaborFootageOverride) : '',
                              );
                              setEditingLaborFootage(true);
                            }}
                            className={
                              'tnum font-medium ' +
                              (isPreFreeze ? 'cursor-text hover:text-primary' : 'cursor-default')
                            }
                            title={isPreFreeze ? 'Click to edit' : 'Locked once sent'}
                          >
                            {job.laborFootage} {unit}
                          </button>
                        )}
                      </div>
                      {/* Insulation SF breakdown — the per-batting-line area that
                          drives the auto-derived footage. Hidden once overridden
                          (the manual value wins) or when there are no batting lines. */}
                      {job.productLine === 'insulation' && battingLines.length > 0 && (
                        <div className="rounded-sm bg-secondary/30 px-2 py-1.5 text-xs">
                          <div className="mb-1 flex items-center justify-between text-muted-foreground">
                            <span>{isOverridden ? 'Batting area (manual override active)' : 'Batting area (auto)'}</span>
                            <span className="tnum">{derivedSf.toLocaleString()} SF</span>
                          </div>
                          <ul className="space-y-0.5">
                            {battingLines.map((e) => (
                              <li key={e.id} className="flex items-center justify-between gap-2 text-muted-foreground">
                                <span className="truncate">{e.skuCode ?? e.skuName ?? 'SKU'}</span>
                                <span className="tnum whitespace-nowrap">
                                  {e.enteredSf != null
                                    ? `${e.enteredSf.toLocaleString()} SF → ${e.quantity} bag${e.quantity === 1 ? '' : 's'}`
                                    : `${e.quantity} × ${e.coverageSf} = ${(e.quantity * (e.coverageSf ?? 0)).toLocaleString()} SF`}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {isPreFreeze && isOverridden && (
                        <button
                          type="button"
                          onClick={() => laborFootageMutation.mutate(null)}
                          disabled={laborFootageMutation.isPending}
                          className="text-xs text-muted-foreground hover:text-primary"
                        >
                          {job.productLine === 'insulation'
                            ? 'Revert to derived (from batts)'
                            : 'Revert to estimate'}
                        </button>
                      )}
                      {laborFootageMutation.error && (
                        <div className="text-xs text-destructive">
                          {laborFootageMutation.error instanceof Error
                            ? laborFootageMutation.error.message
                            : 'Failed to save'}
                        </div>
                      )}
                    </div>
                    {hasLabor && (
                      <div className="flex items-center justify-between rounded-md border border-border bg-secondary/30 px-3 py-2">
                        <span className="text-sm font-medium text-foreground">Labor total</span>
                        <span className="tnum text-base font-bold text-foreground">
                          {formatCents(job.quoteLaborChargeCents)}
                        </span>
                      </div>
                    )}
                    </>
                  );
                })()}
                {(() => {
                  // Mirrors the inline Quote-amount edit gate + the
                  // worker's POST /api/quotes 409 QUOTE_LOCKED check.
                  // Edits during 'quoted' are allowed — customer-side
                  // renegotiation. The backend auto-reverts the job to
                  // 'estimated' on edit so the next manual quoted-PATCH
                  // freezes a fresh PDF. Lock fires at 'accepted' once
                  // the BoM is the install contract.
                  // Read-only "View Profitability" once the quote is sent (quoted+
                  // incl. disqualified) — shows the frozen snapshot, no editing.
                  // Editable "Edit estimate" only at created/estimated. Mode is
                  // purely status-driven and reverts when the job goes back to
                  // estimated. Matches the labor live/frozen regime.
                  const isReadOnly = job.status !== 'created' && job.status !== 'estimated';
                  return (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => setQuoteDialogOpen(true)}
                      title={
                        isReadOnly
                          ? 'View the frozen quote profitability (read-only)'
                          : undefined
                      }
                    >
                      <Calculator className="h-4 w-4" />
                      {isReadOnly ? 'View Profitability' : 'Edit estimate'}
                    </Button>
                  );
                })()}
              </div>
            )}
          </Card>

          {/* ---- CARD 3: QUOTE (all quote logic consolidated) ---- */}
          <Card className="p-5">
            <button
              onClick={() => setQuoteExpanded(!quoteExpanded)}
              className="flex w-full items-center gap-1.5 text-left"
            >
              <ChevronDown className={'h-3.5 w-3.5 text-muted-foreground transition-transform' + (quoteExpanded ? '' : ' -rotate-90')} />
              <h3 className="eyebrow flex items-center gap-1.5">
                <FileText className="h-3 w-3" />
                Quote
              </h3>
            </button>

            {quoteExpanded && (
              <div className="mt-3 space-y-3">
                {/* Quote amount summary — click to edit while the
                    customer is still negotiating (created / estimated /
                    quoted). Editing in 'quoted' auto-reverts the job
                    to 'estimated' server-side; office must re-send via
                    status=quoted to freeze a fresh PDF. Locked once
                    accepted (line items are now the install contract). */}
                {hasQuote && (() => {
                  const isPreFreeze =
                    job.status === 'created' ||
                    job.status === 'estimated' ||
                    job.status === 'quoted';
                  // Per-job frozen tax rate (migration 0048). 0 for all jobs
                  // created after sales tax was removed; legacy jobs keep the
                  // rate they were quoted at. Drives BOTH the displayed total
                  // and the post-tax→pre-tax back-calc on inline edit — they
                  // MUST use the same rate or an edit would silently reprice.
                  const taxRatePct = job.quoteTaxRatePctSnapshot;
                  // Rev 1.2: the editable Quote value is the COMBINED
                  // material + labor total (server `quoteTotalCents` = materials
                  // + tax + labor). On edit we back-calc the materials base
                  // THROUGH labor so `quoteAmountCents` stays "materials" and the
                  // total keeps updating when labor changes: labor is added AFTER
                  // tax server-side, so subtract the (untaxed) labor charge first,
                  // then detax. Clamp ≥ 0 (a combined below the labor floors
                  // materials at 0 → the field snaps up to the labor charge).
                  const totalCents = job.quoteTotalCents;
                  const commitEdit = () => {
                    const cleaned = quoteAmountDraft.replace(/[^0-9.]/g, '');
                    const dollars = parseFloat(cleaned);
                    if (!Number.isFinite(dollars) || dollars < 0) {
                      setEditingQuoteAmount(false);
                      return;
                    }
                    const enteredTotalCents = Math.round(dollars * 100);
                    if (enteredTotalCents === totalCents) {
                      setEditingQuoteAmount(false);
                      return;
                    }
                    // Back-calc materials THROUGH labor AND misc: quoteTotalCents =
                    // materials(+tax) + labor + misc, so strip the undiscounted misc addend
                    // too (migration 0075) or the materials base would absorb it.
                    const subtotalCents = postTaxToSubtotalCents(
                      Math.max(
                        0,
                        enteredTotalCents - job.quoteLaborChargeCents - job.miscTotalCents,
                      ),
                      taxRatePct,
                    );
                    quoteAmountMutation.mutate(subtotalCents);
                  };
                  return (
                    <div className="rounded-md border border-border bg-secondary/30 px-3 py-2">
                      {editingQuoteAmount ? (
                        <Input
                          autoFocus
                          inputMode="decimal"
                          value={quoteAmountDraft}
                          onChange={(e) => setQuoteAmountDraft(e.target.value)}
                          onBlur={commitEdit}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              commitEdit();
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              setEditingQuoteAmount(false);
                            }
                          }}
                          disabled={quoteAmountMutation.isPending}
                          className="tnum h-9 text-xl font-bold"
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            if (!isPreFreeze) return;
                            setQuoteAmountDraft(
                              (totalCents / 100).toFixed(2),
                            );
                            setEditingQuoteAmount(true);
                          }}
                          className={
                            'tnum text-xl font-bold text-foreground ' +
                            (isPreFreeze
                              ? 'cursor-text hover:text-primary'
                              : 'cursor-default')
                          }
                          title={
                            isPreFreeze
                              ? 'Quote total (material + labor) — click to edit'
                              : 'Locked once sent'
                          }
                        >
                          {formatCents(totalCents)}
                        </button>
                      )}
                      {quoteAmountMutation.error && (
                        <div className="mt-1 text-xs text-destructive">
                          {quoteAmountMutation.error instanceof Error
                            ? quoteAmountMutation.error.message
                            : 'Failed to save'}
                        </div>
                      )}
                      {job.quoteSentAt && (
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          Sent {formatDate(job.quoteSentAt)}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Miscellaneous lines — read-only on created/estimated (the Pricing
                    Calculator owns misc editing there); editable once the SKU BoM is
                    frozen (quoted…invoiced); hidden on completed/disqualified with no misc. */}
                <JobMiscLinesSection
                  job={job}
                  editable={
                    job.status !== 'created' &&
                    job.status !== 'estimated' &&
                    job.status !== 'completed' &&
                    job.status !== 'disqualified'
                  }
                />

                {/* Action buttons */}
                <div className="space-y-2">
                  {generatePdfMutation.isPending && (
                    <div className="text-xs text-muted-foreground">
                      Regenerating PDF…
                    </div>
                  )}

                  {/* Disqualify moved into the stepper's trailing red pill.
                      "Mark accepted" stays as a contextual CTA on the Quote
                      card (the stepper's Accepted pill does the same). */}
                  {job.status === 'quoted' && hasQuote && (
                    <div className="mt-2 flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1"
                        disabled={statusMutation.isPending}
                        onClick={() =>
                          statusMutation.mutate({ jobId: job.id, status: 'accepted' })
                        }
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Mark accepted
                      </Button>
                    </div>
                  )}
                </div>

                {generatePdfMutation.error && (
                  <div className="text-xs text-destructive">
                    {generatePdfMutation.error instanceof Error
                      ? generatePdfMutation.error.message
                      : 'PDF generation failed'}
                  </div>
                )}

                {/* PDF viewer */}
                {(job.hasDraftPdf || job.hasFrozenPdf) && (
                  <div className="border-t border-border pt-3">
                    {(pdfVersion === 'draft' ? job.hasDraftPdf : job.hasFrozenPdf) ? (
                      <iframe
                        key={iframeKey}
                        src={`${getJobPdfUrl(id!, pdfVersion)}&t=${iframeKey}`}
                        className="w-full h-[500px] rounded-md border"
                        title="Quote PDF"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-border bg-secondary/20 py-8">
                        <FileText className="h-6 w-6 text-taupe" />
                        <span className="text-sm text-muted-foreground">No PDF yet</span>
                      </div>
                    )}

                    {job.hasFrozenPdf && (
                      <div className="mt-3 flex items-center gap-2">
                        <Badge variant="secondary">
                          Sent {job.quoteSentAt ? formatDate(job.quoteSentAt) : ''}
                        </Badge>
                        <button
                          className="text-xs text-primary underline-offset-2 hover:underline"
                          onClick={() => setPdfVersion(pdfVersion === 'frozen' ? 'draft' : 'frozen')}
                        >
                          {pdfVersion === 'frozen' ? 'View draft version' : 'View sent version'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* ======== RIGHT COLUMN ======== */}
        <div className="space-y-6">

          {/* ---- CARD 4: SCHEDULE ----
              Renders for ALL jobs/statuses. Estimate (left) + Install (right);
              for insulation the Install half delegates to the Phases card
              ("Managed in Install Phases below") rather than hiding the card.
              Warranty / Follow-up Visits is folded in BELOW the columns and
              self-gates to installed-or-later (see WarrantyVisitsCard). */}
          {/* Schedule card — stacked sub-sections: Estimate, then Install.
              For phased jobs (insulation always; gutter post-0061) the Install
              sub-section folds in the Install Phases + Remaining Pool cards
              (embedded). Legacy non-phased gutter keeps the inline calendar
              install scheduler. Warranty visits stay folded in last. */}
          <Card className="p-5">
            <h3 className="eyebrow mb-3 flex items-center gap-1.5">
              <Calendar className="h-3 w-3" />
              Schedule
            </h3>

            {/* Estimate — 3-window state machine (scheduleable at 'created',
                revert-only at 'estimated', read-only history once 'quoted'+). */}
            <JobScheduleSection
              jobId={job.id}
              purpose="estimate"
              heading="Estimate"
              canSchedule={job.status === 'created'}
              readOnly={job.status !== 'created' && job.status !== 'estimated'}
            />

            {/* Install. `id="phases"` is the deep-link scroll target from the
                calendar-drawer "Schedule via Install Phases" CTA. */}
            {usePhasedInstall ? (
              <div id="phases" className="scroll-mt-4">
                <PhasesTimelineCard jobId={job.id} jobStatus={job.status} embedded />
                <RemainingPoolCard jobId={job.id} embedded />
              </div>
            ) : isInstallSchedulable(job.status) ? (
              <div className="mt-4 border-t border-border pt-4">
                <JobScheduleSection jobId={job.id} purpose="install" heading="Install" />
              </div>
            ) : job.status === 'installed' ||
              job.status === 'invoiced' ||
              job.status === 'completed' ||
              job.status === 'disqualified' ? (
              <div className="mt-4 border-t border-border pt-4">
                <JobScheduleSection jobId={job.id} purpose="install" heading="Install" readOnly />
              </div>
            ) : (
              <div className="mt-4 border-t border-border pt-4">
                <h3 className="eyebrow mb-2 flex items-center gap-1.5">
                  <Calendar className="h-3 w-3" />
                  Install
                </h3>
                <p className="text-sm text-muted-foreground">
                  Available once the job is accepted.
                </p>
              </div>
            )}

            {/* Warranty / Follow-up Visits — self-gates to installed-or-later
                (button) + shows existing visits read-only after a revert. */}
            <WarrantyVisitsCard jobId={job.id} jobStatus={job.status} embedded />
          </Card>

          {/* Migration 0052: the per-phase Invoices card was removed. The
              computed per-phase amount + the office-entered QBO invoice
              number now live inline in the Phases card (PhasesTimelineCard).
              Gutter jobs capture the QBO number on the installed→invoiced
              modal below. */}

          {/* ---- CREW UTILIZATION (migration 0056; card titled "Crew Utilization") ----
              Crew time entries (travel + install hours) + the single per-job
              actual installed footage. Capture-only — no pay math. Office
              view/correct surface; crew self-report via Telegram /hours. */}
          <TimeEntriesCard
            jobId={job.id}
            jobStatus={job.status}
            productLine={job.productLine}
            actualInstalledFootage={job.actualInstalledFootage}
            quoteFootage={job.laborFootage}
          />

          {/* ---- INVOICED (migration 0059) — record/edit QBO # + invoiced amount;
              recommended default = the quote total (insulation) or materials +
              ACTUAL-installed labor (gutter). Shows ONLY after the job is invoiced
              (invoicedAt set by the Mark-invoiced modal, both product lines;
              cleared on revert). */}
          {job.invoicedAt != null && <JobInvoiceCard job={job} phased={isPhasedJob} />}

          {/* CARD 5 (Completed/pulled materials) removed — pull-removal 2026-06-09. */}

          {/* ---- CARD 6: ATTACHMENTS ---- */}
          <Card className="p-5">
            <h3 className="eyebrow mb-3 flex items-center gap-1.5">
              <Paperclip className="h-3 w-3" />
              Attachments
            </h3>

            {/* Sketch — thumbnail in card, click opens lightbox with rotate */}
            {job.hasSketchPhoto && (
              <div className="mb-4">
                <button
                  onClick={() => setSketchExpanded(!sketchExpanded)}
                  className="flex items-center gap-1.5 text-left"
                >
                  <ChevronDown className={'h-3.5 w-3.5 text-muted-foreground transition-transform' + (sketchExpanded ? '' : ' -rotate-90')} />
                  <span className="text-xs font-medium text-foreground">Sketch</span>
                </button>
                {sketchExpanded && (
                  <img
                    src={getJobSketchUrl(id!)}
                    alt="Job sketch"
                    className="mt-2 max-h-48 w-auto rounded-md border cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => {
                      setSketchRotation(0);
                      setSketchZoom(1);
                      setSketchPan({ x: 0, y: 0 });
                      setSketchLightbox(true);
                    }}
                  />
                )}
              </div>
            )}

            {/* Document library — office uploads here; crew via Telegram. */}
            <JobDocumentsSection jobId={id!} />
          </Card>
        </div>
      </div>

      {/* ============================================================ */}
      {/* NOTES (full-width, spans both columns)                       */}
      {/* ============================================================ */}
      {/* Single free-text field shared between the office (manual) and
          the bot (auto-append on crew events). Autosaves on blur. */}
      <Card className="mt-4 p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="eyebrow flex items-center gap-1.5">
            <StickyNote className="h-3 w-3" />
            Notes
          </h3>
          <div className="flex items-center gap-1 text-[0.625rem] text-taupe">
            {notesMutation.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving…
              </>
            ) : notesMutation.isSuccess ? (
              <>
                <CheckIcon className="h-3 w-3 text-primary" />
                Saved
              </>
            ) : null}
          </div>
        </div>
        <textarea
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          onBlur={() => {
            const current = job.notes ?? '';
            if (notesDraft !== current) {
              notesMutation.mutate(notesDraft);
            }
          }}
          placeholder="Add notes about this job. The bot also auto-appends a line when a crew submits an estimate or confirms install."
          className="min-h-[140px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm font-mono leading-relaxed ring-offset-background placeholder:text-taupe focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
      </Card>

      {/* ============================================================ */}
      {/* SKETCH LIGHTBOX                                              */}
      {/* ============================================================ */}
      {sketchLightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setSketchLightbox(false);
          }}
          onMouseMove={handlePanMove}
          onMouseUp={handlePanEnd}
          onMouseLeave={handlePanEnd}
          onWheel={(e) => {
            e.preventDefault();
            setSketchZoom((z) => {
              const next = Math.min(5, Math.max(0.25, z + (e.deltaY < 0 ? 0.15 : -0.15)));
              if (next <= 1) setSketchPan({ x: 0, y: 0 });
              return next;
            });
          }}
        >
          <div className="relative flex flex-col items-center gap-3">
            <img
              src={getJobSketchUrl(id!)}
              alt="Job sketch"
              className={
                'max-h-[85vh] max-w-[90vw] rounded-lg shadow-2xl transition-transform duration-200 select-none ' +
                (sketchZoom > 1 ? 'cursor-grab' : '') +
                (isPanning ? ' !cursor-grabbing' : '')
              }
              style={{
                transform: `translate(${sketchPan.x}px, ${sketchPan.y}px) rotate(${sketchRotation}deg) scale(${sketchZoom})`,
              }}
              draggable={false}
              onMouseDown={handlePanStart}
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSketchZoom((z) => Math.max(0.25, z - 0.25))}
                className="flex items-center justify-center rounded-full bg-white/90 p-2 text-foreground shadow-lg hover:bg-white transition-colors"
              >
                <ZoomOut className="h-4 w-4" />
              </button>
              <button
                onClick={() => setSketchRotation((r) => (r + 90) % 360)}
                className="flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-sm font-medium text-foreground shadow-lg hover:bg-white transition-colors"
              >
                <RotateCw className="h-4 w-4" />
                Rotate
              </button>
              <button
                onClick={() => setSketchZoom((z) => Math.min(5, z + 0.25))}
                className="flex items-center justify-center rounded-full bg-white/90 p-2 text-foreground shadow-lg hover:bg-white transition-colors"
              >
                <ZoomIn className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* DIALOGS                                                      */}
      {/* ============================================================ */}

      {/* Invoice-number modal — fires on installed → invoiced (gutter).
          Migration 0052: the app no longer generates invoices. The office
          invoices the job in QuickBooks, reads the amount below, and types
          the QBO invoice number here. The worker stamps invoiced_at to NOW()
          (the overdue-payment aging clock). Number is optional — the office
          can record it later by re-running the transition. */}
      <Dialog
        open={invoiceSentOpen}
        onOpenChange={(open) => {
          if (!open && !statusMutation.isPending) setInvoiceSentOpen(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark invoiced</DialogTitle>
            <DialogDescription>
              Invoice this job in QuickBooks, then record the QBO invoice
              number here. Marking invoiced starts the overdue-payment clock
              on the Today page.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="flex items-center justify-between rounded-md bg-muted px-3 py-2">
              <span className="eyebrow">Amount to invoice</span>
              <span className="text-base font-semibold tabular-nums">
                {formatCents(recommendedInvoiceCents(job))}
              </span>
            </div>
            <div>
              <label className="eyebrow mb-1.5 block">QBO Invoice #</label>
              <Input
                value={invoiceNumberDraft}
                onChange={(e) => setInvoiceNumberDraft(e.target.value)}
                placeholder="e.g. 1042"
                disabled={statusMutation.isPending}
                className="w-48"
              />
            </div>
            {statusMutation.error && (
              <p className="mt-2 text-xs text-destructive">
                {statusMutation.error instanceof Error
                  ? statusMutation.error.message
                  : 'Failed to update status'}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setInvoiceSentOpen(false)}
              disabled={statusMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                statusMutation.mutate(
                  {
                    jobId: job.id,
                    status: 'invoiced',
                    invoiceNumber: invoiceNumberDraft.trim() || null,
                  },
                  { onSuccess: () => setInvoiceSentOpen(false) },
                )
              }
              disabled={statusMutation.isPending}
            >
              {statusMutation.isPending ? 'Saving…' : 'Mark invoiced'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disqualify confirm */}
      <ConfirmDialog
        open={disqualifyOpen}
        title="Disqualify this job?"
        description="The job will be removed from the calendar and frozen at its current stage. You can reopen it later — all line items, quote PDFs, and history are preserved."
        confirmLabel="Disqualify"
        variant="destructive"
        isPending={statusMutation.isPending}
        error={
          statusMutation.error instanceof Error ? statusMutation.error.message : null
        }
        onConfirm={() => {
          statusMutation.mutate(
            { jobId: job.id, status: 'disqualified' },
            { onSuccess: () => setDisqualifyOpen(false) },
          );
        }}
        onCancel={() => setDisqualifyOpen(false)}
      />

      {/* Confirm reverting a finished job backward (un-closing a
          completed/invoiced job — easy to do with a single pill click). */}
      <ConfirmDialog
        open={revertConfirm !== null}
        title="Step this job back?"
        description="This reopens a finished job and clears its closed / invoice-sent date. You can advance it forward again afterward."
        confirmLabel="Step back"
        variant="destructive"
        isPending={statusMutation.isPending}
        error={
          statusMutation.error instanceof Error ? statusMutation.error.message : null
        }
        onConfirm={() => {
          if (revertConfirm) {
            statusMutation.mutate(
              { jobId: job.id, status: revertConfirm },
              { onSuccess: () => setRevertConfirm(null) },
            );
          }
        }}
        onCancel={() => setRevertConfirm(null)}
      />

      <ConfirmDialog
        open={reassignConfirm}
        title="Reassign this job to a different customer?"
        description="This job already has a sent quote. Reassigning changes who the job belongs to, but the already-sent quote PDF won't change — it will still show the previous customer. Re-quote afterward to refresh it."
        confirmLabel="Reassign"
        isPending={customerMutation.isPending}
        error={
          customerMutation.error instanceof Error ? customerMutation.error.message : null
        }
        onConfirm={() => customerMutation.mutate(customerDraft)}
        onCancel={() => setReassignConfirm(false)}
      />

      {/* Quote builder dialog */}
      <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
        <DialogContent
          className="max-w-[76rem] max-h-[85vh] content-start overflow-y-auto"
          // Don't let the dialog auto-focus the first line item's SKU picker on
          // open — Radix focuses the first tabbable child, which fires
          // SkuSearchSelect's onFocus={openList} and pops the ~2,935-option
          // dropdown, stealing focus and risking accidental overwrites when the
          // estimate already has rows. Picker still opens on explicit click/tab.
          onOpenAutoFocus={(e) => e.preventDefault()}
          // Min-height floor so the empty (no-SKU-yet) estimate modal isn't
          // cramped — ~2x the prior floor per office feedback. Also blunts the
          // shrink/jump when the results panel toggles. min(…,85vh) clamps to
          // the max-h so it can't overflow a short viewport.
          style={{ minHeight: 'min(840px, 85vh)' }}
        >
          <DialogHeader>
            <DialogTitle>
              {job.status !== 'created' && job.status !== 'estimated'
                ? `Profitability — ${job.displayName}`
                : `Edit estimate — ${job.displayName}`}
            </DialogTitle>
            <DialogDescription>
              {job.status !== 'created' && job.status !== 'estimated'
                ? 'The quote as sent — read-only profitability from the frozen snapshot.'
                : 'Select SKUs and quantities, then save the quote to this job.'}
            </DialogDescription>
          </DialogHeader>
          <PricingCalculator
            jobId={job.id}
            initialCustomerId={job.customerId}
            readOnly={job.status !== 'created' && job.status !== 'estimated'}
            initialMiscLines={job.miscLines}
            initialLineItems={job.fieldEstimates
              .filter((e) => e.skuId !== null && e.status === 'confirmed')
              .map((e) => ({
                // Seed the editor skuId via the SHARED predicate (seedSkuIdForEstimate),
                // which uses the SAME "expand?" decision as the picker's option list — so
                // only true preset SKUs (KN3646) get the composite key; a normal batt that
                // carries an rValue (B09E/B22EV) stays a bare id and resolves in the picker.
                skuId: seedSkuIdForEstimate({
                  skuCode: e.skuCode,
                  skuId: e.skuId as string,
                  rValue: e.rValue,
                }),
                // Batting lines: the calculator's field is SF, so seed the actual
                // SF (entered_sf), not the stored bags. Legacy/non-batting → qty.
                quantity: String(e.enteredSf ?? e.quantity),
                // Preserve the per-line R-value so a re-quote keeps the PDF "— R-XX"
                // suffix (preset lines also carry R in the composite key above).
                rValue: e.rValue,
              }))}
            onQuoteSaved={() => {
              setQuoteDialogOpen(false);
              generatePdfMutation.mutate();
            }}
            onCancel={() => setQuoteDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// SHARED SUB-COMPONENTS
// ============================================================

// Recommended invoice amount (advisory — the card stores whatever the office types).
// BOTH product lines: the FROZEN quote total (materials + tax + QUOTED labor). Crew
// utilization (actualInstalledFootage) must NOT feed any invoice figure (user
// 2026-06-16) — it feeds only the Crew Utilization page. The per-phase amounts
// apportion this same quoteTotal, so the job-level recommendation IS the quote total.
// (Gutter previously recommended materials + tax + ACTUAL-installed-LF labor; that
// actual-LF basis was dropped to honor the Crew-Utilization-only rule — any
// actual-vs-quoted LF difference is adjusted by hand in QuickBooks.)
function recommendedInvoiceCents(job: JobDetail): number {
  return job.quoteTotalCents;
}

// ---- INVOICED card (migration 0059) ----
// Records the office-entered QBO invoice number + the amount actually invoiced.
// Recommended amount = quote total (insulation) or materials + tax + ACTUAL-installed
// labor (gutter) — see recommendedInvoiceCents; shown as placeholder + hint, the office
// types what they invoiced. Job-level for both product lines. SEAM: insulation ALSO
// invoices per phase (Phases card, which apportions this same quote total) — this
// job-level figure is a convenience record, NOT auto-reconciled with the per-phase rows.
function JobInvoiceCard({ job, phased }: { job: JobDetail; phased: boolean }) {
  const queryClient = useQueryClient();
  const recommendedCents = recommendedInvoiceCents(job);

  const [numberDraft, setNumberDraft] = useState(job.externalInvoiceNumber ?? '');
  const [amountDraft, setAmountDraft] = useState(
    job.invoicedAmountCents != null ? (job.invoicedAmountCents / 100).toFixed(2) : '',
  );
  useEffect(() => {
    setNumberDraft(job.externalInvoiceNumber ?? '');
  }, [job.externalInvoiceNumber]);
  useEffect(() => {
    setAmountDraft(job.invoicedAmountCents != null ? (job.invoicedAmountCents / 100).toFixed(2) : '');
  }, [job.invoicedAmountCents]);

  const mutation = useMutation({
    mutationFn: (input: { invoiceNumber?: string | null; amountCents?: number | null }) =>
      setJobInvoice(job.id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.jobs.detail(job.id) });
      void queryClient.invalidateQueries({ queryKey: qk.jobs.all() });
    },
  });

  const commitNumber = () => {
    const next = numberDraft.trim() === '' ? null : numberDraft.trim();
    if (next === (job.externalInvoiceNumber ?? null)) return;
    mutation.mutate({ invoiceNumber: next });
  };
  const commitAmount = () => {
    const cleaned = amountDraft.replace(/[^0-9.]/g, '');
    if (cleaned === '') {
      if (job.invoicedAmountCents == null) return; // nothing stored → nothing to clear
      mutation.mutate({ amountCents: null });
      return;
    }
    const dollars = parseFloat(cleaned);
    if (!Number.isFinite(dollars) || dollars < 0) {
      setAmountDraft(job.invoicedAmountCents != null ? (job.invoicedAmountCents / 100).toFixed(2) : '');
      return;
    }
    const cents = Math.round(dollars * 100);
    if (cents === (job.invoicedAmountCents ?? null)) return;
    mutation.mutate({ amountCents: cents });
  };

  return (
    <Card className="p-5">
      <h3 className="eyebrow mb-3 flex items-center gap-1.5">
        <Receipt className="h-3 w-3" />
        Invoiced
      </h3>
      <div className="space-y-3">
        {/* Two halves, single row: QBO Invoice # | Amount invoiced */}
        <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="eyebrow mb-1.5 block">QBO Invoice #</label>
          <Input
            value={numberDraft}
            onChange={(e) => setNumberDraft(e.target.value)}
            onBlur={commitNumber}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            disabled={mutation.isPending}
            placeholder="e.g. 1042"
            className="h-9"
          />
        </div>
        <div>
          <label className="eyebrow mb-1.5 block">Amount invoiced</label>
          <Input
            value={amountDraft}
            inputMode="decimal"
            onChange={(e) => setAmountDraft(e.target.value)}
            onBlur={commitAmount}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            disabled={mutation.isPending}
            placeholder={(recommendedCents / 100).toFixed(2)}
            className="tnum h-9"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Recommended (materials + labor):{' '}
            <span className="font-medium tnum text-foreground">{formatCents(recommendedCents)}</span>
          </p>
        </div>
        </div>
        {phased && (
          <p className="text-xs text-taupe">
            Job-level figure — this job also bills per phase (see Phases above).
          </p>
        )}
        {mutation.error && (
          <p className="text-xs text-destructive">
            {mutation.error instanceof Error ? mutation.error.message : 'Failed to save'}
          </p>
        )}
      </div>
    </Card>
  );
}

// ---- Miscellaneous lines (migration 0075) ----
// Free-form "Miscellaneous" quote lines shown inside the Quote card. On
// created/estimated the Pricing Calculator owns misc editing (two-phase save), so
// here it renders READ-ONLY (editable=false). Once the SKU BoM is frozen (quoted…
// invoiced) this is the ONLY misc write surface — add/remove go straight to the
// dedicated endpoints and NEVER touch field_estimates (the frozen install contract
// stays byte-identical). Hidden entirely on completed/disqualified with no misc.
function JobMiscLinesSection({ job, editable }: { job: JobDetail; editable: boolean }) {
  const queryClient = useQueryClient();
  const [desc, setDesc] = useState('');
  const [uom, setUom] = useState<CanonicalUom | ''>('');
  const [qty, setQty] = useState('');
  const [amount, setAmount] = useState('');

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: qk.jobs.detail(job.id) });
    void queryClient.invalidateQueries({ queryKey: qk.jobs.all() });
    // Phased invoicing reads ['job-phases'] — refresh so the phase totals reflect misc.
    void queryClient.invalidateQueries({ queryKey: ['job-phases', job.id] });
  };
  const addMutation = useMutation({
    mutationFn: (input: MiscLineCreateInput) => postMiscLine(job.id, input),
    onSuccess: () => {
      setDesc('');
      setUom('');
      setQty('');
      setAmount('');
      invalidate();
    },
  });
  const removeMutation = useMutation({
    mutationFn: (lineId: string) => deleteMiscLine(job.id, lineId),
    onSuccess: invalidate,
  });

  const amountNum = Number(amount);
  const canAdd =
    desc.trim().length > 0 &&
    amount.trim() !== '' &&
    Number.isFinite(amountNum) &&
    amountNum >= 0;
  const submitAdd = () => {
    if (!canAdd) return;
    addMutation.mutate({
      description: desc.trim(),
      amountCents: Math.round(amountNum * 100),
      ...(uom ? { uom } : {}),
      ...(qty.trim() !== '' && Number.isFinite(Number(qty)) ? { quantity: Number(qty) } : {}),
    });
  };

  // Nothing to show: no misc lines AND no editing affordance.
  if (!editable && job.miscLines.length === 0) return null;

  return (
    <div className="rounded-md border border-border px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <span className="eyebrow">Miscellaneous</span>
        {job.miscLines.length > 0 && (
          <span className="tnum text-sm font-semibold text-foreground">
            {formatCents(job.miscTotalCents)}
          </span>
        )}
      </div>
      {job.miscLines.length > 0 ? (
        <ul className="divide-y divide-border">
          {job.miscLines.map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-2 py-1.5">
              <div className="min-w-0">
                <div className="truncate text-sm text-foreground">{m.description}</div>
                {m.quantity != null && (
                  <div className="tnum text-xs text-taupe">
                    {m.quantity.toLocaleString()}
                    {m.uom ? ` ${m.uom}` : ''}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="tnum text-sm text-foreground">{formatCents(m.amountCents)}</span>
                {editable && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => removeMutation.mutate(m.id)}
                    disabled={removeMutation.isPending}
                    className="h-7 w-7 p-0 text-muted-foreground hover:border-destructive hover:text-destructive"
                    aria-label="Remove misc line"
                    title="Remove misc line"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">No miscellaneous lines.</p>
      )}
      {editable && (
        <div className="space-y-2 border-t border-border pt-2">
          <div className="flex items-center gap-2">
            <Input
              placeholder="Description (e.g. Custom fascia repair)"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              className="h-9 flex-1"
            />
            <select
              value={uom}
              onChange={(e) => setUom(e.target.value as CanonicalUom | '')}
              className="h-9 w-[4.5rem] shrink-0 rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label="Unit (optional)"
            >
              <option value="">—</option>
              {CANONICAL_UOMS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
            <Input
              type="number"
              min="0"
              step="any"
              placeholder="Qty"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="h-9 w-16 shrink-0"
              aria-label="Quantity (optional)"
            />
            <div className="relative w-28 shrink-0">
              <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-sm text-muted-foreground">
                $
              </span>
              <Input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submitAdd();
                  }
                }}
                className="tnum h-9 w-full pl-5 text-right"
                aria-label="Amount"
              />
            </div>
            <Button
              size="sm"
              onClick={submitAdd}
              disabled={!canAdd || addMutation.isPending}
              className="h-9 shrink-0"
            >
              <Plus className="h-3.5 w-3.5" />
              Add
            </Button>
          </div>
          <p className="text-xs text-taupe">
            Adds a flat, discount-exempt line — the SKU bill of materials isn’t changed.
          </p>
          {addMutation.error && (
            <p className="text-xs text-destructive">
              {addMutation.error instanceof Error ? addMutation.error.message : 'Failed to add'}
            </p>
          )}
          {removeMutation.error && (
            <p className="text-xs text-destructive">
              {removeMutation.error instanceof Error
                ? removeMutation.error.message
                : 'Failed to remove'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function FieldEstimateRow({ estimate }: { estimate: JobFieldEstimateRow }) {
  const name = estimate.skuName
    ? `${estimate.skuName}${estimate.variant ? ` · ${estimate.variant}` : ''}${
        estimate.rValue ? ` — ${estimate.rValue}` : ''
      }`
    : estimate.freehandText ?? 'Unknown';
  return (
    <div className="flex items-center justify-between px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-foreground truncate">{name}</div>
        {estimate.skuCode && (
          <div className="text-xs text-muted-foreground font-mono">
            {estimate.skuCode}
            {estimate.enteredSf != null && ` · ${formatInt(estimate.enteredSf)} SF`}
          </div>
        )}
      </div>
      <div className="text-right shrink-0 ml-4">
        <div className="tnum text-sm font-semibold text-foreground">
          {estimate.quantity} {unitLabel(estimate.unit)}
        </div>
        <div className="tnum text-xs text-muted-foreground">
          {formatCents(estimate.priceCentsAtEstimate)}
        </div>
      </div>
    </div>
  );
}
